<template>
    <div>
        <DataTable
            :perPage="itemsPerPage"
            :columns="tableColumns"
            :items="paginatedItems"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableDragDrop="true"
            :enableMultiSelect="true"
            @page-changed="handlePageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @drag-end="handleDragEnd"
            @sort="handleSort"
            @per-page-changed="handlePerPageChange"
            :sortBy="sortBy"
            :sortOrder="sortOrder"
        >
            <!-- Toolbar -->
            <template #toolbar>
                <div class="ppv2-flex md:ppv2-flex-row ppv2-flex-col ppv2-gap-2 ppv2-justify-between ppv2-items-center ppv2-mb-6">
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-3">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                        </div>

                        <!-- Export Button -->
                        <ExportSelect
                            :options="[
                                { label: 'CSV', value: 'csv' },
                                { label: 'Excel', value: 'excel' },
                            ]"
                            :disabled="isExporting"
                            @export-selected="exportData"
                        />

                        <!-- Filter Button -->
                        <button
                            @click="openFilterModal"
                            class="filterButton ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-text-black hover:ppv2-bg-opacity-80 ppv2-transition-all"
                        >
                            <LayoutGrid :size="17" />
                            <span class="poppinsLight" :style="{ fontSize: normalizeFontSize(15) }">
                                {{ $t('products.actions.filter') }}
                            </span>
                            <span
                                v-if="activeFiltersCount > 0"
                                class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-bg-[#7D2DBD] ppv2-text-white ppv2-text-[10px] ppv2-w-5 ppv2-h-5 ppv2-rounded-full poppinsSemiBold"
                            >
                                {{ activeFiltersCount }}
                            </span>
                        </button>
                        <AdvancedFilterModal
                            v-model="showFilterModal"
                            :initialFilters="advancedFilters"
                            :availableFilters="['tags', 'status', 'price_range']"
                            :tags="filterData.tags"
                            @apply="handleAdvancedFilters"
                        />

                        <FilterByType :selected="selectedType" @filter-selected="selectType" />
                    </div>

                    <div class="ppv2-relative" ref="addDropdown">
                        <BaseSplitButton
                            :label="$t('common.add')"
                            :icon="Plus"
                            :showDropdown="showAddDropdown"
                            @click="showAddDropdown = !showAddDropdown"
                            @toggle="showAddDropdown = !showAddDropdown"
                        />

                        <div
                            v-if="showAddDropdown"
                            class="menuDropdown ppv2-absolute ppv2-right-0 ppv2-top-full ppv2-mt-2 ppv2-w-48 ppv2-bg-white ppv2-rounded-xl ppv2-shadow-xl ppv2-z-50 ppv2-border ppv2-border-gray-100 ppv2-overflow-hidden"
                        >
                            <button
                                @click="openCreateProductTab()"
                                class="ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-3 hover:ppv2-bg-gray-50 ppv2-transition-colors ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-text-gray-700"
                            >
                                <Plus :size="16" class="ppv2-text-gray-400" />
                                <span class="poppinsRegular ppv2-text-sm">{{ $t('food.carte.add_product') }}</span>
                            </button>
                            <button
                                @click="openCreateTab()"
                                class="ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-3 hover:ppv2-bg-gray-50 ppv2-transition-colors ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-text-gray-700 ppv2-border-t ppv2-border-gray-50"
                            >
                                <Plus :size="16" class="ppv2-text-gray-400" />
                                <span class="poppinsRegular ppv2-text-sm">{{ $t('food.carte.add_pack') }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Column: Name -->
            <template #column-name="{ item }">
                <span class="ppv2-underline ppv2-text-black">{{ item.name }}</span>
            </template>

            <!-- Column: Price -->
            <template #column-price="{ item }">
                <span>{{ formatPrice(item.price) }} {{ $symbolCurrency }}</span>
            </template>

            <!-- Column: Category -->
            <template #column-category="{ item }">
                <span
                    class="ppv2-inline-block ppv2-px-3 ppv2-py-1 ppv2-rounded-lg poppinsRegular"
                    :style="{
                        backgroundColor: item.category_color,
                        color: 'black',
                    }"
                >
                    {{ item.category_name }}
                </span>
            </template>

            <!-- Column: Stock -->
            <template #column-stock="{ item }">
                <span
                    v-if="!item.manage_stock && item.type !== 'pack'"
                    class="ppv2-underline ppv2-text-black"
                    :style="{ fontSize: normalizeFontSize(14) }"
                >
                    &infin;
                </span>
                <span
                    v-else-if="item.type !== 'pack'"
                    class="ppv2-flex ppv2-items-center ppv2-gap-1"
                    :class="item.stock < 5 ? 'poppinsSemiBold ppv2-text-[#911414]' : 'poppinsRegular ppv2-text-black'"
                    :style="{ fontSize: normalizeFontSize(14) }"
                >
                    {{ item.stock }}
                    <span v-if="item.stock < 5">
                        <TriangleAlert :size="18" color="#911414" />
                    </span>
                </span>
                <span v-else>-</span>
            </template>

            <!-- Column: Tags -->
            <template #column-tags="{ item }">
                <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-1">
                    <span v-for="tag in item.tags || []" :key="tag" class="ppv2-underline ppv2-text-black">#{{ tag }}</span>
                </div>
            </template>

            <!-- Column: Status -->
            <template #column-status="{ item }">
                <button
                    @click="toggleStatus(item)"
                    class="ppv2-min-w-[125px] ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-transition-all"
                    :class="
                        item.status === 'actif'
                            ? 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
                            : 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
                    "
                >
                    <div
                        class="ppv2-w-9 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-transition-all"
                        :class="
                            item.status === 'actif'
                                ? 'ppv2-bg-[#206A64] ppv2-justify-end'
                                : 'ppv2-bg-[#4E4E4E] ppv2-justify-start'
                        "
                    >
                        <div class="ppv2-w-3.5 ppv2-h-3.5 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5"></div>
                    </div>
                    <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                        item.status === 'actif' ? 'Activé' : 'Désactivé'
                    }}</span>
                </button>
            </template>

            <!-- Column: Type -->
            <template #column-type="{ item }">
                <span
                    class="ppv2-px-2 ppv2-py-1 ppv2-rounded-lg ppv2-text-xs ppv2-uppercase poppinsSemiBold"
                    :class="
                        item.type === 'pack' ? 'ppv2-bg-blue-100 ppv2-text-blue-600' : 'ppv2-bg-orange-100 ppv2-text-orange-600'
                    "
                >
                    {{ item.type === 'pack' ? 'Formule' : 'Produit' }}
                </span>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3">
                    <button @click="openEditTab(item)" class="ppv2-p-1 hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded">
                        <SquarePen :size="18" />
                    </button>
                    <button
                        @click="duplicateItem(item)"
                        class="ppv2-p-1 hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded"
                    >
                        <CopyPlus :size="18" />
                    </button>
                    <button
                        @click="deleteSingleItem(item)"
                        class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded"
                    >
                        <Trash2 :size="18" />
                    </button>
                </div>
            </template>

            <!-- Bulk Actions -->
            <template #bulk-actions="{ selectedCount }">
                <span class="poppinsRegular textGray">{{ selectedCount }} sélectionné(s)</span>
                <BaseButton
                    variant="outline"
                    @click="openBulkTagModal"
                    customClass="!ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Hash :size="18" /><span :style="{ fontSize: normalizeFontSize(14) }">Modifier tag</span>
                </BaseButton>
                <BaseButton
                    variant="dangerOutline"
                    @click="deleteSelectedItems"
                    customClass="!ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Trash2 :size="18" /><span :style="{ fontSize: normalizeFontSize(14) }">{{ $t('common.delete') }}</span>
                </BaseButton>
            </template>
        </DataTable>

        <CustomDialog v-model="showDeletePopup" :defaultTitle="$t('common.delete')" noScroll>
            <template #content>
                <p class="ppv2-text-center">{{ deletePopupDescription }}</p>
            </template>
            <template #actions>
                <BaseButton variant="outline" @click="showDeletePopup = false">{{ $t('common.cancel') }}</BaseButton>
                <BaseButton variant="danger" @click="confirmDelete">{{ $t('common.delete') }}</BaseButton>
            </template>
        </CustomDialog>

        <!-- Duplicate Popup -->
        <CustomDialog v-model="showDuplicatePopup" :defaultTitle="$t('common.duplicate')" height="250px" minHeight="250px">
            <template #content>
                <p class="ppv2-text-center">{{ duplicatePopupDescription }}</p>
            </template>
            <template #actions>
                <BaseButton variant="outline" @click="showDuplicatePopup = false">{{ $t('common.cancel') }}</BaseButton>
                <BaseButton variant="primary" @click="confirmDuplicate">{{ $t('common.confirm') }}</BaseButton>
            </template>
        </CustomDialog>

        <!-- Bulk Tag Modal -->
        <CustomDialog v-model="showBulkTagModal" defaultTitle="Modifier les tags" heightClass="ppv2-max-h-[60vh]">
            <template #content>
                <p class="ppv2-text-sm ppv2-text-gray-600 ppv2-mb-4">Gérez les tags pour {{ selectedItems.length }} formule(s)</p>
                <div class="ppv2-flex ppv2-flex-col" :style="{ gap: normalizeSize(16, true) }">
                    <div class="ppv2-flex ppv2-mt-4" :style="{ gap: normalizeSize(16, true) }">
                        <label class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-cursor-pointer">
                            <input type="radio" v-model="bulkTagForm.mode" value="add" class="ppv2-accent-[#7D2DBD]" />
                            <span class="poppinsRegular">{{ $t('common.add') }}</span>
                        </label>
                        <label class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-cursor-pointer">
                            <input type="radio" v-model="bulkTagForm.mode" value="remove" class="ppv2-accent-[#7D2DBD]" />
                            <span class="poppinsRegular">{{ $t('common.remove') }}</span>
                        </label>
                        <label class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-cursor-pointer">
                            <input type="radio" v-model="bulkTagForm.mode" value="replace" class="ppv2-accent-[#7D2DBD]" />
                            <span class="poppinsRegular">{{ $t('common.replace') }}</span>
                        </label>
                    </div>
                    <div class="ppv2-flex ppv2-flex-col ppv2-gap-2">
                        <BaseSelect
                            v-model="bulkTagForm.tags"
                            :options="tagsOptions"
                            optionLabel="label"
                            :reduce="(opt) => opt.label"
                            multiple
                            taggable
                            placeholder="Sélectionner des tags"
                            dropdownPlacement="top"
                            class="ppv2-w-full"
                        />
                    </div>
                </div>
            </template>
            <template #actions>
                <BaseButton variant="outline" @click="showBulkTagModal = false">{{ $t('common.cancel') }}</BaseButton>
                <BaseButton variant="primary" @click="submitBulkTags">{{ $t('common.apply') }}</BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import { LayoutGrid, ChevronDown, Plus, SquarePen, CopyPlus, Trash2, Hash, TriangleAlert, Percent } from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from '../Experience/components/Search.vue'
import FilterByType from '../../Shared/FilterByType.vue'
import ExportSelect from '../../Shared/ExportSelect.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import BaseSplitButton from '../../Shared/BaseSplitButton.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import DataTable from '../../Shared/DataTable.vue'
import AdvancedFilterModal from '../../Shared/AdvancedFilterModal.vue'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../utils/perPage'

export default {
    name: 'BoFoodV3Packs',
    components: {
        LayoutGrid,
        ChevronDown,
        Plus,
        SquarePen,
        CopyPlus,
        Trash2,
        Hash,
        Search,
        ExportSelect,
        BaseButton,
        BaseSplitButton,
        BaseSelect,
        CustomDialog,
        DataTable,
        AdvancedFilterModal,
        FilterByType,
    },
    props: {
        establishment: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            Plus,
            loading: false,
            searchQuery: '',
            currentPage: 1,
            itemsPerPage: getPerPage('food_packs_items_per_page', 10),
            selectedType: 'pack',
            selectedItems: [],
            sortBy: 'order',
            sortOrder: 'asc',
            showAddDropdown: false,
            showFilterModal: false,
            showDeletePopup: false,
            deletePopupDescription: '',
            deleteAction: null,
            itemToDelete: null,
            // Duplicate Popup
            showDuplicatePopup: false,
            duplicatePopupDescription: '',
            itemToDuplicate: null,
            items: [],
            totalPages: 1,
            isExporting: false,
            isSavingPositions: false,
            advancedFilters: {
                status: 'all',
                tag_ids: [],
                price_min: null,
                price_max: null,
            },
            filterData: {
                tags: [],
            },
            // Bulk Tag Modal
            showBulkTagModal: false,
            tagsOptions: [],
            bulkTagForm: {
                mode: 'add',
                tags: [],
            },
        }
    },
    computed: {
        tableColumns() {
            return [
                { key: 'name', label: 'Nom', sortable: true, type: 'text' },
                { key: 'price', label: 'Prix', sortable: true, type: 'number' },
                { key: 'category', label: 'Catégorie', sortable: false, type: 'text' },
                { key: 'stock', label: 'Stock', sortable: true, type: 'number' },
                { key: 'tags', label: 'Tags', sortable: false, type: 'text' },
                { key: 'status', label: 'État', sortable: true, type: 'badge' },
                { key: 'type', label: 'Type', sortable: true, type: 'badge' },
                { key: 'actions', label: 'Actions', sortable: false, type: 'actions' },
            ]
        },
        paginatedItems() {
            return this.items
        },
        activeFiltersCount() {
            let count = 0
            if (this.advancedFilters.status !== 'all') count++
            if (this.advancedFilters.tag_ids?.length > 0) count++
            if (this.advancedFilters.price_min !== null && this.advancedFilters.price_min !== '') count++
            if (this.advancedFilters.price_max !== null && this.advancedFilters.price_max !== '') count++
            return count
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        formatPrice(p) {
            return Number(p || 0)
                .toFixed(2)
                .replace('.', ',')
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        handlePerPageChange(val) {
            this.itemsPerPage = Number(val)
            setPerPage('food_packs_items_per_page', this.itemsPerPage)
            this.currentPage = 1
            this.fetchData()
        },
        handleSearchUpdate(val) {
            this.searchQuery = val
            this.currentPage = 1
            this.fetchData()
        },
        toggleItemSelection(id) {
            const idx = this.selectedItems.indexOf(id)
            idx > -1 ? this.selectedItems.splice(idx, 1) : this.selectedItems.push(id)
        },
        toggleSelectAll() {
            this.selectedItems = this.selectedItems.length === this.items.length ? [] : this.items.map((i) => i.id)
        },
        async toggleStatus(item) {
            const prev = item.status
            item.status = prev === 'actif' ? 'inactif' : 'actif'
            try {
                await axios.patch('/back-office/v3/food/update-status', {
                    id: item.original_id || item.id,
                    active: item.status === 'actif',
                    type: item.type || 'pack',
                })
            } catch {
                item.status = prev
            }
        },
        handleSort(key) {
            if (this.sortBy === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortBy = key
                this.sortOrder = 'asc'
            }
            this.fetchData()
        },
        async openFilterModal() {
            if (this.filterData.tags.length === 0) {
                try {
                    const response = await axios.get('/back-office/v3/food/get-filters-data')
                    this.filterData = response.data.data
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                }
            }
            this.showFilterModal = true
        },
        handleAdvancedFilters(filters) {
            this.advancedFilters = filters
            this.currentPage = 1
            this.fetchData()
        },
        deleteSingleItem(item) {
            this.deleteAction = 'single'
            this.itemToDelete = item
            const typeLabel = item.type === 'pack' ? 'cette formule' : 'ce produit'
            this.deletePopupDescription = `Supprimer ${typeLabel} ?`
            this.showDeletePopup = true
        },
        deleteSelectedItems() {
            if (!this.selectedItems.length) return
            this.deleteAction = 'multiple'
            this.deletePopupDescription = `Supprimer ${this.selectedItems.length} formule(s) ?`
            this.showDeletePopup = true
        },
        async confirmDelete() {
            if (this.deleteAction === 'single') {
                try {
                    const endpoint =
                        this.itemToDelete.type === 'pack' ? '/back-office/v3/food/packs/delete' : '/back-office/v3/food/delete'
                    await axios.delete(endpoint, { data: { ids: [this.itemToDelete.original_id || this.itemToDelete.id] } })
                    this.fetchData()
                    this.selectedItems = []
                    this.showDeletePopup = false
                    this.$emit('refresh-stats')
                } catch {
                    alert('Erreur')
                }
            } else {
                try {
                    const foods = this.selectedItems
                        .map((id) => this.items.find((i) => i.id === id))
                        .filter((i) => i && i.type === 'food')
                        .map((i) => i.original_id || i.id)
                    const packs = this.selectedItems
                        .map((id) => this.items.find((i) => i.id === id))
                        .filter((i) => i && i.type === 'pack')
                        .map((i) => i.original_id || i.id)

                    if (foods.length) await axios.delete('/back-office/v3/food/delete', { data: { ids: foods } })
                    if (packs.length) await axios.delete('/back-office/v3/food/packs/delete', { data: { ids: packs } })

                    this.fetchData()
                    this.selectedItems = []
                    this.showDeletePopup = false
                    this.$emit('refresh-stats')
                } catch {
                    alert('Erreur')
                }
            }
        },
        async openBulkTagModal() {
            if (this.selectedItems.length === 0) return
            this.bulkTagForm = { mode: 'add', tags: [] }
            if (this.tagsOptions.length === 0) {
                await this.fetchTags()
            }
            this.showBulkTagModal = true
        },
        async fetchTags() {
            try {
                const res = await fetch('/back-office/v3/food/categories/tags-list')
                const json = await res.json()
                this.tagsOptions = (json.data || []).map((tag) => ({ label: tag }))
            } catch (e) {
                console.error(e)
            }
        },
        async submitBulkTags() {
            try {
                const items = this.selectedItems.map((id) => this.items.find((i) => i.id === id)).filter(Boolean)
                const foods = items.filter((i) => i.type === 'food').map((i) => i.original_id || i.id)
                const packs = items.filter((i) => i.type === 'pack').map((i) => i.original_id || i.id)

                const requests = []
                const tagData = { tags: this.bulkTagForm.tags, mode: this.bulkTagForm.mode }

                if (foods.length) {
                    requests.push(
                        axios.post('/back-office/v3/food/bulk-action', { ids: foods, action: 'update_tags', data: tagData })
                    )
                }
                if (packs.length) {
                    requests.push(
                        axios.post('/back-office/v3/food/packs/bulk-action', { ids: packs, action: 'update_tags', data: tagData })
                    )
                }

                await Promise.all(requests)
                this.showBulkTagModal = false
                this.selectedItems = []
                this.fetchData()
            } catch (e) {
                console.error(e)
                alert('Erreur lors de la mise à jour des tags')
            }
        },
        openEditTab(item) {
            const id = item.type === 'pack' ? item.original_id || item.id : item.uuid || item.id
            const type = item.type === 'pack' ? 'pack' : 'food'
            window.location.href = `/back-office/v3/food/edit/${id}/${type}`
        },
        openCreateTab() {
            window.location.href = '/back-office/v3/food/create/pack'
        },
        openCreateProductTab() {
            window.location.href = '/back-office/v3/food/create/food'
        },
        selectType(type) {
            this.selectedType = type
            this.currentPage = 1
            this.fetchData()
        },
        handleClickOutside(event) {
            if (this.showAddDropdown && this.$refs.addDropdown && !this.$refs.addDropdown.contains(event.target)) {
                this.showAddDropdown = false
            }
        },
        async fetchData() {
            try {
                const params = {
                    type: this.selectedType,
                    search: this.searchQuery,
                    currentPage: this.currentPage,
                    itemsPerPage: this.itemsPerPage,
                    sortBy: this.sortBy,
                    sortOrder: this.sortOrder,
                    ...this.advancedFilters,
                }
                const response = await axios.get('/back-office/v3/food/list-combined', { params })
                this.items = (response.data.data || []).map((item) => ({
                    ...item,
                    original_id: item.id,
                    id: item.uuid || `${item.type}-${item.id}`, // Ensure unique keys
                }))
                this.totalPages = response.data.last_page || 1
            } catch (e) {
                console.error(e)
            }
        },
        async handleDragEnd(event) {
            const { oldIndex, newIndex } = event

            if (oldIndex === newIndex) return

            // Move the item locally
            const movedItem = this.items.splice(oldIndex, 1)[0]
            this.items.splice(newIndex, 0, movedItem)

            // Calculate start order index based on pagination
            const startOrder = (this.currentPage - 1) * this.itemsPerPage
            const changedPositions = []

            // Re-assign order based on new index
            this.items.forEach((item, index) => {
                const newOrder = startOrder + index

                // If order changed, add to update list
                changedPositions.push({
                    id: item.id,
                    order: newOrder,
                })
                item.order = newOrder
            })

            // Save if there are changes
            if (changedPositions.length > 0) {
                await this.savePositions(changedPositions)
            }
        },
        async savePositions(positions) {
            if (this.isSavingPositions) return
            this.isSavingPositions = true

            try {
                await axios.put('/back-office/v3/food/packs/update-positions', {
                    positions,
                })
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error saving positions:', error)
                this.fetchData()
            } finally {
                this.isSavingPositions = false
            }
        },
        async exportData(format = 'csv') {
            if (this.isExporting) return

            const params = {
                format,
                type: this.selectedType,
                search: this.searchQuery,
                ...this.advancedFilters,
            }

            this.isExporting = true
            try {
                const response = await axios.get('/back-office/v3/food/export', {
                    params,
                    responseType: 'blob',
                })

                const url = window.URL.createObjectURL(new Blob([response.data]))
                const link = document.createElement('a')
                link.href = url

                const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
                let fileName = `packs_export_${format}.${format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'csv'}`
                if (contentDisposition) {
                    const fileNameMatch = contentDisposition.match(/filename=(?:"([^"]+)"|([^;]+))/)
                    if (fileNameMatch) {
                        fileName = fileNameMatch[1] || fileNameMatch[2]
                    }
                }

                link.setAttribute('download', fileName)
                document.body.appendChild(link)
                link.click()

                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
            } catch (e) {
                console.error(e)
                alert("Une erreur est survenue lors de l'exportation.")
            } finally {
                this.isExporting = false
            }
        },
        duplicateItem(item) {
            this.itemToDuplicate = item
            const typeLabel = item.type === 'pack' ? 'cette formule' : 'ce produit'
            this.duplicatePopupDescription = `Êtes-vous sûr de vouloir dupliquer ${typeLabel} ?`
            this.showDuplicatePopup = true
        },
        async confirmDuplicate() {
            if (!this.itemToDuplicate) return
            try {
                const isPack = this.itemToDuplicate.type === 'pack'
                const endpoint = isPack ? '/back-office/v3/food/packs/duplicate' : '/back-office/v3/food/duplicate'
                const response = await axios.post(endpoint, { id: this.itemToDuplicate.original_id || this.itemToDuplicate.id })
                if (response.data.success) {
                    const newItem = response.data.data
                    const newId = isPack ? newItem.id : newItem.uuid
                    const typeLabel = isPack ? 'Formule' : 'Produit'
                    this.$toast?.success(`${typeLabel} dupliquée avec succès`)
                    window.location.href = `/back-office/v3/food/edit/${newId}/${isPack ? 'pack' : 'food'}`
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error duplicating pack:', error)
                alert('Erreur lors de la duplication')
            } finally {
                this.showDuplicatePopup = false
                this.itemToDuplicate = null
            }
        },
    },
    mounted() {
        this.fetchData()
        document.addEventListener('click', this.handleClickOutside)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
    },
}
</script>

<style scoped lang="scss">
.textGray {
    color: #1a1f37;
}

.dark .textGray {
    color: #bfbfbf;
}
</style>
